import {
  computed,
  watch,
  toValue,
  ref,
  type Ref,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue';
import { toString, uniq, uniqBy, sortBy } from 'lodash-es';
import { getValue, areValuesEqual } from '../../../shared/utils';
import type { Path, TrackBy } from '../../../shared/types';
interface Group {
  key: string;
  title?: string | null;
}
export interface OptionsListItem<O = unknown, V = unknown> {
  label: string;
  disabled: boolean;
  value: V;
  selected: boolean;
  group: string | null;
  origin: O | null;
  missing: boolean;
}
export interface OptionsListGroup<O = unknown, V = unknown> {
  key: string;
  title: string | null;
  options: OptionsListItem<O, V>[];
}
type UseOptionsListOptions<O = unknown, V = unknown> = {
  options?: MaybeRefOrGetter<O[]>;
  asyncOptions?: (query?: string) => Promise<O[]>;
  groups?: MaybeRefOrGetter<Group[]>;
  optionDisabled?: Path<O> | ((option: O) => boolean);
  optionLabel?: Path<O> | ((option: O) => string);
  optionValue?: Path<O> | ((option: O) => V);
  optionGroup?: Path<O> | ((option: O) => string);
  hydrateValue?: (value: V[]) => Promise<O[]>;
  trackValueBy?: TrackBy<V>;
  optionsLimit?: number;
  search?: MaybeRefOrGetter<string>;
  selectionMode?: 'single' | 'multiple';
  showSelectedFirst?: boolean;
  selected?: MaybeRefOrGetter<V | null | V[]>;
  updateSelected?: (value: V | null | V[]) => void;
};
interface UseOptionsListReturn<O = unknown, V = unknown> {
  optionsList: ComputedRef<OptionsListItem<O, V>[]>;
  optionsGroups: ComputedRef<OptionsListGroup<O, V>[]>;
  optionsGroupsWithEmpty: ComputedRef<OptionsListGroup<O, V>[]>;
  selectedOptions: ComputedRef<OptionsListItem<O, V>[]>;
  selectedOption: ComputedRef<OptionsListItem<O, V> | null>;
  allOptionsSelected: ComputedRef<boolean>;
  loadingMissingOptions: ComputedRef<boolean>;
  loadingOptions: ComputedRef<boolean>;
  loadOptions: () => Promise<void>;
  selectOption: (options: OptionsListItem<O, V>) => void;
  unselectOption: (options: OptionsListItem<O, V>) => void;
  toggleOption: (options: OptionsListItem<O, V>) => void;
  isOptionSelected: (option: O) => boolean;
  isOptionDisabled: (option: O) => boolean;
  isValueSelected: (value: V) => boolean;
  selectAll: () => void;
  toggleAll: () => void;
}
export function useOptionsList<O = unknown, V = unknown>(
  options: UseOptionsListOptions<O, V>,
): UseOptionsListReturn<O, V> {
  function normalizeForTextSearch(value: unknown): string {
    return toString(value).toLowerCase().trim();
  }
  function getOptionLabel(option: O): string {
    const { optionLabel } = options;
    return toString(optionLabel ? getValue(option, optionLabel) : option);
  }
  function getOptionValue(option: O): V {
    const { optionValue } = options;
    return (optionValue ? getValue(option, optionValue) : option) as V;
  }
  function getOptionGroup(option: O): string | null {
    const { optionGroup } = options;
    return optionGroup ? (getValue(option, optionGroup) ?? null) : null;
  }
  function isValueSelected(value: V): boolean {
    const { trackValueBy, selectionMode = 'single' } = options;
    const selected = toValue(options.selected);
    if (selectionMode === 'single') {
      return areValuesEqual<V>(value, selected as V, trackValueBy);
    } else if (selectionMode === 'multiple' && Array.isArray(selected)) {
      return selected.some((item) => areValuesEqual<V>(item, value, trackValueBy));
    }
    return false;
  }
  function isOptionDisabled(option: O): boolean {
    const { optionDisabled } = options;
    return !!(optionDisabled ? getValue(option, optionDisabled) : false);
  }
  function isOptionSelected(option: O): boolean {
    const value = getOptionValue(option);
    return isValueSelected(value);
  }
  function formatOptions(items: O[]): OptionsListItem<O, V>[] {
    const result = items.map<OptionsListItem<O, V>>((item) => ({
      label: getOptionLabel(item),
      disabled: isOptionDisabled(item),
      value: getOptionValue(item),
      selected: isOptionSelected(item),
      group: getOptionGroup(item),
      origin: item,
      missing: false,
    }));
    if (options.showSelectedFirst) {
      return result.sort((a, b) => {
        if (a.selected === b.selected) {
          return 0;
        }
        return a.selected ? -1 : 1;
      });
    }
    return result;
  }
  const search = computed(() => toValue(options.search));
  const rawOptionsList = ref<O[]>([]) as Ref<O[]>;
  const optionsList = computed<OptionsListItem<O, V>[]>(() => {
    let result = formatOptions(rawOptionsList.value);
    if (options.asyncOptions) {
      return result;
    }
    if (search.value) {
      const searchQuery = normalizeForTextSearch(search.value);
      result = result.filter(({ label }) => normalizeForTextSearch(label).includes(searchQuery));
    }
    return result.slice(0, options.optionsLimit ?? 300);
  });
  const loadingOptions = ref(false);
  let optionsRequestId = 0;
  async function loadOptions() {
    const requestId = Date.now();
    optionsRequestId = requestId;
    if (typeof options.asyncOptions !== 'function') {
      return;
    }
    loadingOptions.value = true;
    try {
      const result = await options.asyncOptions(toValue(options.search));
      if (requestId === optionsRequestId) {
        rawOptionsList.value = result ?? [];
      }
    } catch (error) {
    } finally {
      if (requestId === optionsRequestId) {
        loadingOptions.value = false;
      }
    }
  }
  watch(
    () => toValue(options.options),
    (value) => {
      if (!options.asyncOptions && value) {
        rawOptionsList.value = value ?? [];
      }
    },
    { deep: true, immediate: true },
  );
  watch(search, () => {
    if (options.asyncOptions) {
      loadOptions();
    }
  });
  const autoGeneratedGroups = computed(() =>
    uniq(optionsList.value.map(({ group }) => group)).filter((item): item is string => !!item),
  );
  const optionsGroupsWithEmpty = computed(() => {
    const groups = toValue(options.groups);
    if (Array.isArray(groups)) {
      return groups.map(({ key, title }) => ({
        key,
        title: title ?? null,
        options: optionsList.value.filter((item) => item.group === key),
      }));
    }
    return autoGeneratedGroups.value.map((group) => ({
      key: group,
      title: group,
      options: optionsList.value.filter((item) => item.group === group),
    }));
  });
  const optionsGroups = computed(() =>
    optionsGroupsWithEmpty.value.filter((group) => group.options.length > 0),
  );
  let cachedSelectedOptions: OptionsListItem<O, V>[] = [];
  const loadedOptions = ref<OptionsListItem<O, V>[]>([]) as Ref<OptionsListItem<O, V>[]>;
  const selectedOptions = computed<OptionsListItem<O, V>[]>(() => {
    const { trackValueBy } = options;
    const selected = toValue(options.selected);
    if (selected === null || typeof selected === 'undefined') {
      return [];
    }
    return (Array.isArray(selected) ? selected : [selected]).map((value) => {
      if (!options.optionValue) {
        const valueAsOption = value as unknown as O;
        return {
          label: getOptionLabel(valueAsOption),
          value,
          selected: true,
          disabled: isOptionDisabled(valueAsOption),
          group: getOptionGroup(valueAsOption),
          origin: valueAsOption,
          missing: false,
        };
      }
      const selectedOption = [
        ...optionsList.value,
        ...loadedOptions.value,
        ...cachedSelectedOptions,
      ].find((item) => {
        return areValuesEqual<V>(value, item.value, trackValueBy);
      });
      if (selectedOption) {
        return {
          ...selectedOption,
          selected: true,
        };
      }
      return {
        label: toString(value),
        disabled: false,
        value,
        selected: true,
        group: null,
        origin: null,
        missing: true,
      };
    });
  });
  const selectedOption = computed(() => selectedOptions.value[0] ?? null);
  const allOptionsSelected = computed(() => {
    const { selectionMode = 'single' } = options;
    if (selectionMode === 'single') {
      return false;
    }
    return !optionsList.value.some(({ selected }) => !selected);
  });
  function selectOption(option: OptionsListItem<O, V>) {
    if (typeof options.updateSelected !== 'function') {
      return;
    }
    let selected = toValue(options.selected);
    if (options.selectionMode === 'single') {
      options.updateSelected(option.value);
      return;
    }
    if (!Array.isArray(selected)) {
      selected = [];
    }
    options.updateSelected([...selected, option.value]);
  }
  function unselectOption(option: OptionsListItem<O, V>) {
    if (typeof options.updateSelected !== 'function') {
      return;
    }
    let selected = toValue(options.selected);
    if (options.selectionMode === 'single') {
      options.updateSelected(null);
      return;
    }
    if (!Array.isArray(selected)) {
      selected = [];
    }
    options.updateSelected(
      selected.filter((item) => !areValuesEqual<V>(item, option.value, options.trackValueBy)),
    );
  }
  function toggleOption(option: OptionsListItem<O, V>) {
    if (typeof options.updateSelected !== 'function') {
      return;
    }
    if (option.selected) {
      unselectOption(option);
      return;
    }
    selectOption(option);
  }
  function selectAll() {
    if (options.selectionMode === 'single' || typeof options.updateSelected !== 'function') {
      return;
    }
    options.updateSelected(optionsList.value.map(({ value }) => value));
  }
  function toggleAll() {
    if (options.selectionMode === 'single') {
      return;
    }
    if (allOptionsSelected.value) {
      if (typeof options.updateSelected === 'function') {
        options.updateSelected([]);
      }
      return;
    }
    selectAll();
  }
  const loadingMissingOptions = ref(false);
  let missingOptionsRequestId = 0;
  async function hydrateValue() {
    const requestId = Date.now();
    missingOptionsRequestId = requestId;
    if (typeof options.hydrateValue !== 'function') {
      return;
    }
    const missingOptions = cachedSelectedOptions.filter(({ missing }) => missing);
    if (!missingOptions.length) {
      return;
    }
    loadingMissingOptions.value = true;
    try {
      const result = await options.hydrateValue(missingOptions.map(({ value }) => value));
      if (requestId === missingOptionsRequestId && result.length) {
        loadedOptions.value = uniqBy(
          sortBy([...loadedOptions.value, ...formatOptions(result)], ['value', 'missing']),
          'value',
        );
      }
    } catch (error) {
    } finally {
      if (requestId === missingOptionsRequestId) {
        loadingMissingOptions.value = false;
      }
    }
  }
  watch(
    selectedOptions,
    (value) => {
      cachedSelectedOptions = value;
      hydrateValue();
    },
    { deep: true, immediate: true },
  );
  return {
    optionsList: computed(() => optionsList.value),
    optionsGroups,
    optionsGroupsWithEmpty,
    selectedOptions,
    selectedOption,
    allOptionsSelected,
    loadingOptions: computed(() => loadingOptions.value),
    loadingMissingOptions: computed(() => loadingMissingOptions.value),
    loadOptions,
    selectOption,
    unselectOption,
    toggleOption,
    selectAll,
    toggleAll,
    isOptionSelected,
    isOptionDisabled,
    isValueSelected,
  };
}
